// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft,
  ScrollText,
  User,
  Calendar,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Upload,
  Download,
  Trash2,
  FolderPlus,
  Pencil,
  Move,
  LogIn,
  LogOut,
  UserPlus,
  Shield,
  KeyRound,
  PenLine
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PER_PAGE = 20;

const actionTypeIcons = {
  login: LogIn,
  logout: LogOut,
  register: UserPlus,
  upload: Upload,
  download: Download,
  delete_file: Trash2,
  delete_folder: Trash2,
  create_folder: FolderPlus,
  rename: Pencil,
  move: Move,
  sign_document: PenLine,
  update_permissions: Shield,
  reset_password: KeyRound,
  change_password: KeyRound
};

const actionTypeColors = {
  login: 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-950',
  logout: 'text-slate-600 bg-slate-50 dark:text-slate-400 dark:bg-slate-800',
  register: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-950',
  upload: 'text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-950',
  download: 'text-sky-600 bg-sky-50 dark:text-sky-400 dark:bg-sky-950',
  delete_file: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-950',
  delete_folder: 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-950',
  create_folder: 'text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-950',
  rename: 'text-purple-600 bg-purple-50 dark:text-purple-400 dark:bg-purple-950',
  move: 'text-indigo-600 bg-indigo-50 dark:text-indigo-400 dark:bg-indigo-950',
  sign_document: 'text-teal-600 bg-teal-50 dark:text-teal-400 dark:bg-teal-950',
  update_permissions: 'text-orange-600 bg-orange-50 dark:text-orange-400 dark:bg-orange-950',
  reset_password: 'text-pink-600 bg-pink-50 dark:text-pink-400 dark:bg-pink-950',
  change_password: 'text-pink-600 bg-pink-50 dark:text-pink-400 dark:bg-pink-950'
};

export default function AuditLogsPage() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionTypes, setActionTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchActionTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/audit-logs/action-types`);
      setActionTypes(response.data.action_types);
    } catch (error) {
      console.error('Error fetching action types:', error);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('per_page', PER_PAGE);
      if (selectedType && selectedType !== 'all') {
        params.append('action_type', selectedType);
      }
      
      const response = await axios.get(`${API}/audit-logs?${params.toString()}`);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Erro ao carregar logs de auditoria');
    } finally {
      setLoading(false);
    }
  }, [page, selectedType]);

  useEffect(() => {
    fetchActionTypes();
  }, [fetchActionTypes]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(total / PER_PAGE);

  return (
    <Layout>
      <div className="space-y-6" data-testid="audit-logs-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="rounded-xl"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold font-heading text-foreground">
                Log de Auditoria
              </h1>
              <p className="text-sm text-muted-foreground">
                Histórico de todas as atividades do sistema
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={fetchLogs}
            className="gap-2"
            data-testid="refresh-logs-button"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Filter className="w-5 h-5 text-slate-400" />
              <Select value={selectedType} onValueChange={(value) => { setSelectedType(value); setPage(1); }}>
                <SelectTrigger className="w-64" data-testid="filter-action-type">
                  <SelectValue placeholder="Filtrar por tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as atividades</SelectItem>
                  {actionTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">
                {total} registro{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ScrollText className="w-5 h-5" />
              Atividades Recentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <ScrollText className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
                <p className="text-muted-foreground">Nenhum registro encontrado</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Tipo</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Detalhes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => {
                    const IconComponent = actionTypeIcons[log.action_type] || ScrollText;
                    const colorClass = actionTypeColors[log.action_type] || 'text-slate-600 bg-slate-50';
                    
                    return (
                      <TableRow key={log.id} data-testid={`log-row-${log.id}`}>
                        <TableCell>
                          <div className={`p-2 rounded-lg w-fit ${colorClass}`}>
                            <IconComponent className="w-4 h-4" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium text-foreground">{log.action}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                              <User className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">{log.user_name}</p>
                              <p className="text-xs text-muted-foreground">{log.user_email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Calendar className="w-4 h-4" />
                            <span className="text-sm">{formatDate(log.created_at)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs text-muted-foreground max-w-xs truncate">
                            {log.details && Object.entries(log.details).length > 0 ? (
                              Object.entries(log.details).slice(0, 2).map(([key, value]) => (
                                <span key={key} className="mr-2">
                                  {key}: {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                </span>
                              ))
                            ) : (
                              <span className="text-muted-foreground/50">-</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground" data-testid="audit-logs-page-indicator">
                  Página {page} de {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label="Página anterior"
                    data-testid="audit-logs-prev-page"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label="Próxima página"
                    data-testid="audit-logs-next-page"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
